import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

export type WaybillStatus =
  | "pending_payment"
  | "pending_dispatch"
  | "driver_assigned"
  | "arrived_at_pickup"
  | "picked_up"
  | "in_transit"
  | "arrived_at_delivery"
  | "delivered"
  | "delivery_failed"
  | "return_started"
  | "returned"
  | "cancelled";

export type WaybillItem = {
  productId?: string;
  title: string;
  quantity: number;
  imageUrl?: string;
  price?: number;
  variantSummary?: string;
};

export type WaybillLocation = {
  address: string;
  lat?: number;
  lng?: number;
};

export type Waybill = {
  _id: string;
  waybillNumber: string;

  checkoutGroupId: string;
  checkoutGroupOrderCount?: number;

  orderId: string;
  buyerUserId: string;
  sellerUserId: string;
  sellerName?: string;

  packageIndex: number;
  packageCount: number;

  items: WaybillItem[];

  pickupWarehouseId: string;
  pickupWarehouseName: string;
  pickupLocation: WaybillLocation;

  dropoffLocation: WaybillLocation;

  deliveryFee?: number;
  distanceKm?: number;
  billableWeightKg?: number;
  bulkySurcharge?: number;
  driverEarning?: number;

  driverId?: string;
  driverName?: string;
  driverPhone?: string;

  routeBatchId?: string;
  routeSequence?: number;

  status: WaybillStatus;

  assignedAt?: string;
  arrivedAtPickupAt?: string;
  pickedUpAt?: string;
  inTransitAt?: string;
  arrivedAtDeliveryAt?: string;
  deliveredAt?: string;
  deliveryFailedAt?: string;
  returnStartedAt?: string;
  returnedAt?: string;
  cancelledAt?: string;

  failureReason?: string;
  returnReason?: string;

  createdAt?: string;
  updatedAt?: string;
};

export type WaybillFilters = {
  checkoutGroupId?: string;
  orderId?: string;
  sellerUserId?: string;
  buyerUserId?: string;
  driverId?: string;
  routeBatchId?: string;
  status?: WaybillStatus | "";
};

type ApiResponse<T> = {
  success?: boolean;
  data: T;
  message?: string;
};

const buildQueryString = (filters?: WaybillFilters) => {
  const params = new URLSearchParams();

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params.set(key, String(value));
    }
  });

  const query = params.toString();
  return query ? `?${query}` : "";
};

export const waybillApi = createApi({
  reducerPath: "waybillApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_BASE_URL}/api/v1`,
  }),
  tagTypes: ["Waybills"],
  endpoints: (builder) => ({
    getWaybills: builder.query<Waybill[], WaybillFilters | void>({
      query: (filters) => `/waybills${buildQueryString(filters || {})}`,
      transformResponse: (response: ApiResponse<Waybill[]>) => response.data,
      providesTags: ["Waybills"],
    }),

    getWaybillById: builder.query<Waybill, string>({
      query: (id) => `/waybills/${id}`,
      transformResponse: (response: ApiResponse<Waybill>) => response.data,
      providesTags: (_result, _error, id) => [{ type: "Waybills", id }],
    }),

    assignDriverToWaybill: builder.mutation<
      Waybill,
      {
        id: string;
        driverId: string;
        driverName?: string;
        driverPhone?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/waybills/${id}/assign-driver`,
        method: "PATCH",
        body,
      }),
      transformResponse: (response: ApiResponse<Waybill>) => response.data,
      invalidatesTags: ["Waybills"],
    }),

    assignDriverToRouteBatch: builder.mutation<
      { routeBatchId: string; waybills: Waybill[] },
      {
        waybillIds: string[];
        driverId: string;
        driverName?: string;
        driverPhone?: string;
        routeBatchId?: string;
      }
    >({
      query: (body) => ({
        url: "/waybills/route-batch/assign-driver",
        method: "PATCH",
        body,
      }),
      transformResponse: (
        response: ApiResponse<{ routeBatchId: string; waybills: Waybill[] }>,
      ) => response.data,
      invalidatesTags: ["Waybills"],
    }),

    updateWaybillStatus: builder.mutation<
      Waybill,
      {
        id: string;
        status: WaybillStatus;
        failureReason?: string;
        returnReason?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/waybills/${id}/status`,
        method: "PATCH",
        body,
      }),
      transformResponse: (response: ApiResponse<Waybill>) => response.data,
      invalidatesTags: ["Waybills"],
    }),
  }),
});

export const {
  useGetWaybillsQuery,
  useGetWaybillByIdQuery,
  useAssignDriverToWaybillMutation,
  useAssignDriverToRouteBatchMutation,
  useUpdateWaybillStatusMutation,
} = waybillApi;
